import React, { useState, useEffect } from 'react';
import { Form, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Col } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { Site } from '../../types/site';
import { Publisher } from '../../types/publisher';
import { cnpjMask, validaCNPJ } from '../../utils';
import { createSite, updateSite } from '../../api/siteApi';
import { getPublishers } from '../../api/publisherApi';
import { useProfile } from '../../hooks/UserHooks';
import Select from 'react-select';

// Custom styles for react-select
const customSelectStyles = {
    control: (provided: any, state: any) => ({
        ...provided,
        minHeight: '38px',
        borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
        },
    }),
    placeholder: (provided: any) => ({
        ...provided,
        color: '#6c757d',
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: '#212529',
    }),
};

interface AddModalProps {
    site: Site | null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Site) => void;
    onUpdateData: (newData: Site) => void;
}

const AddEditSiteModal = ({ site, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [publishers, setPublishers] = useState<Publisher[]>([]);
    const [mediaKitFile, setMediaKitFile] = useState<File | null>(null);
    const { userProfile } = useProfile();

    // Convert publishers to react-select options
    const publisherOptions = publishers.map(publisher => ({
        value: publisher.id,
        label: publisher.name
    }));

    useEffect(() => {
        // Load publishers if user is not ROLE_PUBLISHER_ADMIN
        if (userProfile?.role !== 'ROLE_PUBLISHER_ADMIN') {
            getPublishers().then(setPublishers).catch(console.error);
        }
    }, [userProfile?.role]);

    const toggleAddModal = () => {
        toggle();
    };

    const handleForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            id: site?.id ? site.id : '',
            publisherId: site?.publisherId ? site.publisherId : '',
            mediaCad: site?.mediaCad ? site.mediaCad : '',
            document: site?.document ? site.document : '',
            site: site?.site ? site.site : '',
            app: site?.app ? site.app : '',
            mediaKitUrl: site?.mediaKitUrl ? site.mediaKitUrl : '',
            mediaKitFile: site?.mediaKitFile ? site.mediaKitFile : '',
            accessibilityResources: site?.accessibilityResources ? site.accessibilityResources : false,
            // users: [],
        },
        validationSchema: Yup.object({
            publisherId: userProfile?.role !== 'ROLE_PUBLISHER_ADMIN' 
                ? Yup.number().required('Publisher é obrigatório')
                : Yup.number().optional(),
            mediaCad: Yup.number().integer().optional(),
            document: Yup.string()
                .optional()
                .test('is-valid-cnpj', 'CNPJ inválido', value => value ? validaCNPJ(value) : true),
            site: Yup.string()
                .test(
                    'site-or-app',
                    'O campo site ou o campo app deve ser preenchido',
                    function (value) {
                        const { app } = this.parent;
                        return (!value && app) || (value && !app);
                    },
                )
                .test(
                    'site',
                    'Site inválido. Deve ser uma URL válida (ex: https://example.com)',
                    function (value: any) {
                        if (!value) {
                            return true;
                        }
                        try {
                            new URL(value.startsWith('http') ? value : `https://${value}`);
                            return true;
                        } catch {
                            return false;
                        }
                    },
                ),
            app: Yup.string()
                .test(
                    'app-or-site',
                    'O campo site ou o campo app deve ser preenchido',
                    function (value) {
                        const { site } = this.parent;
                        return (!value && site) || (value && !site);
                    },
                )
                .test(
                    'app',
                    'O ID do aplicativo deve ser um ID válido da Apple App Store (ex: id1234567890) ou Google Play Store (ex: com.example.app)',
                    function (value: any) {
                        if (!value) {
                            return true;
                        }
                        const appleAppStoreRegex = /^(id)?\d+$/;
                        const googlePlayStoreRegex = /^[a-zA-Z0-9._-]+$/;
                        return appleAppStoreRegex.test(value) || googlePlayStoreRegex.test(value);
                    },
                ),
            mediaKitUrl: Yup.string()
                .nullable()
                .optional()
                .url('URL inválida')
                .test(
                    'media-kit-exclusive',
                    'Apenas um tipo de media kit pode ser fornecido (URL ou arquivo)',
                    function (value) {
                        const { mediaKitFile } = this.parent;
                        return !(value && mediaKitFile);
                    }
                ),
            mediaKitFile: Yup.mixed()
                .nullable()
                .optional()
                .test(
                    'media-kit-exclusive',
                    'Apenas um tipo de media kit pode ser fornecido (URL ou arquivo)',
                    function (value) {
                        const { mediaKitUrl } = this.parent;
                        return !(value && mediaKitUrl);
                    }
                ),
            accessibilityResources: Yup.boolean().required('Recursos de acessibilidade é obrigatório'),
        }),
        onSubmit: (values: any) => {
            setLoading(true);

            if (site) {
                // For update, we need to use the site object with the id
                const siteData = {
                    ...site,
                    ...values,
                    mediaKitFile: mediaKitFile
                };
                updateSite(siteData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Site atualizado com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => { })
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                // For create, we need to provide the required fields
                const siteData = {
                    publisherId: values.publisherId,
                    publisher: publishers.find(p => p.id === values.publisherId)!,
                    mediaCad: values.mediaCad,
                    document: values.document,
                    site: values.site,
                    app: values.app,
                    mediaKitUrl: values.mediaKitUrl,
                    mediaKitFile: mediaKitFile ? mediaKitFile.name : undefined,
                    accessibilityResources: values.accessibilityResources,
                    placements: [],
                    orders: [],
                };
                createSite(siteData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Site cadastrado com sucesso!');
                        onAddData(res);
                    })
                    .catch(() => { })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    return (
        <Modal id="showModal" isOpen={isOpen} toggle={toggleAddModal} fade={false} keyboard={true}
            backdrop={false} centered>
            <Form onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleForm.handleSubmit();
                return false;
            }}>
                <ModalHeader toggle={toggleAddModal} className="bg-light p-3">
                    {site ? 'Editar' : 'Adicionar'} Site
                </ModalHeader>

                <ModalBody>
                    <Row>
                        {userProfile?.role !== 'ROLE_PUBLISHER_ADMIN' && (
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label htmlFor="publisherId" className="form-label">
                                        Publisher *
                                    </Label>
                                    <Select
                                        options={publisherOptions}
                                        value={publisherOptions.find(option => option.value === handleForm.values.publisherId)}
                                        onChange={(selectedOption) => {
                                            handleForm.setFieldValue('publisherId', selectedOption?.value || '');
                                        }}
                                        onBlur={() => handleForm.setFieldTouched('publisherId', true)}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        placeholder="Selecione um publisher"
                                        isClearable
                                        styles={customSelectStyles}
                                    />
                                    {handleForm.touched.publisherId && handleForm.errors.publisherId && (
                                        <div className="invalid-feedback d-block">{handleForm.errors.publisherId}</div>
                                    )}
                                </div>
                            </Col>
                        )}
                        <Col md={userProfile?.role === 'ROLE_PUBLISHER_ADMIN' ? 12 : 6}>
                            <div className="mb-3">
                                <Label htmlFor="mediaCad" className="form-label">
                                    Media CAD
                                </Label>
                                <Input
                                    type="number"
                                    id="mediaCad"
                                    name="mediaCad"
                                    value={handleForm.values.mediaCad}
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    invalid={!!(handleForm.touched.mediaCad && handleForm.errors.mediaCad)}
                                    placeholder="Digite o Media CAD"
                                />
                                {handleForm.touched.mediaCad && handleForm.errors.mediaCad && (
                                    <FormFeedback type="invalid">{handleForm.errors.mediaCad}</FormFeedback>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="site" className="form-label">
                                    Site
                                </Label>
                                <Input
                                    type="url"
                                    id="site"
                                    name="site"
                                    value={handleForm.values.site}
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    invalid={!!(handleForm.touched.site && handleForm.errors.site)}
                                    placeholder="https://example.com"
                                />
                                {handleForm.touched.site && handleForm.errors.site && (
                                    <FormFeedback type="invalid">{handleForm.errors.site}</FormFeedback>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="app" className="form-label">
                                    App
                                </Label>
                                <Input
                                    type="text"
                                    id="app"
                                    name="app"
                                    value={handleForm.values.app}
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    invalid={!!(handleForm.touched.app && handleForm.errors.app)}
                                    placeholder="id1234567890 ou com.example.app"
                                />
                                {handleForm.touched.app && handleForm.errors.app && (
                                    <FormFeedback type="invalid">{handleForm.errors.app}</FormFeedback>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="mediaKitUrl" className="form-label">
                                    Media Kit URL
                                </Label>
                                <Input
                                    type="url"
                                    id="mediaKitUrl"
                                    name="mediaKitUrl"
                                    value={handleForm.values.mediaKitUrl}
                                    onChange={(e) => {
                                        handleForm.setFieldValue('mediaKitUrl', e.target.value);
                                        if (e.target.value) {
                                            handleForm.setFieldValue('mediaKitFile', '');
                                            setMediaKitFile(null);
                                        }
                                    }}
                                    onBlur={handleForm.handleBlur}
                                    invalid={!!(handleForm.touched.mediaKitUrl && handleForm.errors.mediaKitUrl)}
                                    placeholder="https://example.com/media-kit.pdf"
                                />
                                {handleForm.touched.mediaKitUrl && handleForm.errors.mediaKitUrl && (
                                    <FormFeedback type="invalid">{handleForm.errors.mediaKitUrl}</FormFeedback>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="mediaKitFile" className="form-label">
                                    Media Kit Arquivo
                                </Label>
                                <Input
                                    type="file"
                                    id="mediaKitFile"
                                    name="mediaKitFile"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0] || null;
                                        setMediaKitFile(file);
                                        handleForm.setFieldValue('mediaKitFile', file);
                                        if (file) {
                                            handleForm.setFieldValue('mediaKitUrl', '');
                                        }
                                    }}
                                    onBlur={handleForm.handleBlur}
                                    invalid={!!(handleForm.touched.mediaKitFile && handleForm.errors.mediaKitFile)}
                                    accept=".pdf,.doc,.docx"
                                />
                                {handleForm.touched.mediaKitFile && handleForm.errors.mediaKitFile && (
                                    <FormFeedback type="invalid">{handleForm.errors.mediaKitFile}</FormFeedback>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="document" className="form-label">
                                    Documento (CNPJ)
                                </Label>
                                <Input
                                    type="text"
                                    id="document"
                                    name="document"
                                    value={cnpjMask(handleForm.values.document)}
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    invalid={!!(handleForm.touched.document && handleForm.errors.document)}
                                    placeholder="00.000.000/0000-00"
                                />
                                {handleForm.touched.document && handleForm.errors.document && (
                                    <FormFeedback type="invalid">{handleForm.errors.document}</FormFeedback>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="accessibilityResources" className="form-label">
                                    Recursos de Acessibilidade *
                                </Label>
                                <div className="form-check form-switch">
                                    <Input
                                        type="checkbox"
                                        id="accessibilityResources"
                                        name="accessibilityResources"
                                        checked={handleForm.values.accessibilityResources}
                                        onChange={(e) => handleForm.setFieldValue('accessibilityResources', e.target.checked)}
                                        onBlur={handleForm.handleBlur}
                                        className="form-check-input"
                                    />
                                    <Label htmlFor="accessibilityResources" className="form-check-label">
                                        {handleForm.values.accessibilityResources ? 'Ativado' : 'Desativado'}
                                    </Label>
                                </div>
                                {handleForm.touched.accessibilityResources && handleForm.errors.accessibilityResources && (
                                    <div className="invalid-feedback d-block">{handleForm.errors.accessibilityResources}</div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <button
                        type="submit"
                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    >
                        <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                        {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        {site ? 'Editar' : 'Cadastrar'}
                    </button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default AddEditSiteModal;